import { Box, Button, Paper, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { PAGE_SIZE } from 'services/constants/env';
import {
	getConfirmedOrdersForFilterIdRequest,
	getConfirmedOrdersRequest,
	resetConfirmedOrdersForFilterId,
} from 'redux/reducers/orders/reducer';
import {
	getConfirmedOrders,
	getConfirmedOrdersForFilterId,
	getConfirmedOrdersForFilterIdLoader,
	getConfirmedOrdersLoader,
} from 'redux/reducers/orders/selectors';
import { exportOrdersRequest } from 'redux/reducers/emExports/reducer';
import { getExportOrdersLoader } from 'redux/reducers/emExports/selectors';
import { saveUrl } from 'redux/reducers/auth/reducer';
import { getUserInfo } from 'redux/reducers/user/selectors';
import { TConfirmedOrdersRow } from 'redux/reducers/orders/types';
import { IPageResponse, TFilter } from 'services/constants/types';
import { TTableStructure } from 'services/constants/tableStructureTypes';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import {
	getFiltersFromNavigateParams,
	getOrderByFromNavigateParams,
	getTableApiFiltersFromNavigateParams,
	TNavigateFilterBuilder,
} from 'utils/FilterParams';
import { OrdersSummary } from './OrdersSummary';
import { confirmedOrdersStructure } from './confirmed-orders-structure';

/** `schemes` and `externalOrderId` are matched by a substring, not by an exact value. */
const likeFilterBuilder: TNavigateFilterBuilder = (value, column) => [
	{ fieldName: column.selector, value: `%${value}%`, operator: 'like' },
];

const CUSTOM_FILTERS = {
	schemes: likeFilterBuilder,
	externalOrderId: likeFilterBuilder,
};

export const ConfirmedOrders: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { search } = location;
	const navigateParams = queryString.parse(search);
	const { page } = navigateParams;
	const tableStructure = confirmedOrdersStructure as TTableStructure<keyof TConfirmedOrdersRow>[];

	const orders = useSelector(getConfirmedOrders);
	const ordersIsLoading = useSelector(getConfirmedOrdersLoader);
	const ordersFilterId = useSelector(getConfirmedOrdersForFilterId);
	const ordersFilterIdLoading = useSelector(getConfirmedOrdersForFilterIdLoader);
	const exportLoading = useSelector(getExportOrdersLoader);
	const userInfo = useSelector(getUserInfo);

	const [prevSearch, setPrevSearch] = useState(navigateParams?.search);
	const [exportDialogOpen, setExportDialogOpen] = useState(false);

	const tableItems = useMemo((): IPageResponse<TConfirmedOrdersRow> | null => {
		if (!orders) return null;

		const rows = orders.data ?? [];
		const filterIdRow = ordersFilterId?.data?.[0];

		if (filterIdRow?.id) {
			const filteredRows = rows.filter((row) => row.id !== filterIdRow.id);
			return {
				data: [filterIdRow, ...filteredRows],
				totalCount: orders.totalCount ?? 0,
			};
		}

		return {
			data: rows,
			totalCount: orders.totalCount ?? 0,
		};
	}, [orders, ordersFilterId]);

	const dispatchGetConfirmedOrders = () => {
		const searchStringRaw = String(navigateParams?.search ?? '');
		const searchString =
			(searchStringRaw?.length ?? 0) > 2 || (searchStringRaw?.length ?? 0) === 0
				? searchStringRaw
				: '';

		let filters: TFilter[] = [];

		const orderBy = getOrderByFromNavigateParams(tableStructure, navigateParams);

		if (searchString) {
			const idNum = Number(searchString[0] === '#' ? searchString.slice(1) : searchString);
			if (idNum) {
				dispatch(
					getConfirmedOrdersForFilterIdRequest({
						filters: [{ fieldName: 'id', value: idNum, operator: '=' }],
						orderBy,
						pageNumber: 1,
						pageSize: PAGE_SIZE,
					}),
				);
			} else {
				dispatch(resetConfirmedOrdersForFilterId());
			}

			filters = [
				{
					fieldName: 'externalOrderId',
					value: `%${searchString}%`,
					operator: 'like',
				},
			];
		} else {
			filters = getFiltersFromNavigateParams(tableStructure, navigateParams, {
				customFilters: CUSTOM_FILTERS,
			});
		}

		dispatch(
			getConfirmedOrdersRequest({
				filters,
				orderBy,
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
			}),
		);
	};

	useEffect(() => {
		const prevParams = queryString.parse(prevSearch as string);
		const otherPrevParams = Object.entries(prevParams).filter(([key]) => key !== 'search');
		const otherPrevParamsString = otherPrevParams
			.map(([key, value]) => `${key}=${String(value ?? '')}`)
			.join('&');
		const currentParams = queryString.parse(location.search);
		const otherCurrentParams = Object.entries(currentParams).filter(([key]) => key !== 'search');
		const otherCurrentParamsString = otherCurrentParams
			.map(([key, value]) => `${key}=${String(value ?? '')}`)
			.join('&');

		if (
			prevParams.search !== currentParams.search ||
			((prevParams.search as string)?.length ?? 0) === 0 ||
			otherPrevParamsString !== otherCurrentParamsString
		) {
			dispatchGetConfirmedOrders();
		}
		setPrevSearch(location.search);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search]);

	const handleLink = (id: number) => {
		dispatch(saveUrl(window.location.pathname + window.location.search));
		navigate(`/order/${id}`);
	};

	const handleAction = () => {};

	const handleExportOrders = () => {
		const searchStringRaw = String(navigateParams?.search ?? '');
		const searchString = (searchStringRaw?.length ?? 0) > 2 ? searchStringRaw : '';

		dispatch(
			exportOrdersRequest({
				emails: [userInfo?.email ?? ''],
				// The search covers the whole table, so while it is active the column filters
				// are not applied to the list on the screen — nor to the export.
				filter: searchString
					? []
					: getTableApiFiltersFromNavigateParams(tableStructure, navigateParams, {
							customFilters: CUSTOM_FILTERS,
						}),
				orderBy: getOrderByFromNavigateParams(tableStructure, navigateParams),
				searchString,
			}),
		);
		setExportDialogOpen(false);
	};

	return (
		<Box>
			<Paper
				sx={{
					margin: '0px 10px 10px 10px',
					maxWidth: 'calc(100vw - 20px)',
					overflowX: 'auto',
				}}
			>
				<Box sx={{ width: 'auto', minWidth: '1200px', p: '10px 10px 10px 10px' }}>
					<OrdersSummary />
				</Box>
			</Paper>
			<Box sx={{ margin: '0px 10px 0 10px' }}>
				<EasTableWithFilter<TConfirmedOrdersRow>
					tableStructure={tableStructure}
					items={tableItems}
					itemsIsLoading={ordersIsLoading || ordersFilterIdLoading}
					handleAction={handleAction}
					handleLink={handleLink}
					minWidth={1200}
					name="order"
					// highlightedRow={ordersFilterId?.rows?.[0]?.id ? 0 : undefined}
				>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							p: '2px 4px',
						}}
					>
						<Typography
							variant="h1"
							color="primary"
							sx={{ fontSize: '1.1rem', fontWeight: 'bold', textAlign: 'center', ml: 1 }}
						>
							Orders
						</Typography>
						<Button
							variant="contained"
							color="primary"
							size="small"
							disabled={exportLoading}
							onClick={() => setExportDialogOpen(true)}
						>
							Export Orders
						</Button>
					</Box>
				</EasTableWithFilter>
			</Box>
			<ConfirmationDialog
				open={exportDialogOpen}
				setClose={() => setExportDialogOpen(false)}
				handleConfirm={handleExportOrders}
				title="Export Orders"
				text={`The export is being prepared in the background. We will inform you at ${userInfo?.email ?? ''} once complete.
Please note that you can only request one export at a time.`}
				confirmButtonText="Export"
				confirmDisabled={!userInfo?.email}
				confirmLoading={exportLoading}
			/>
		</Box>
	);
};
